import {
  ChevronDown,
  ChevronRight,
  PencilLine,
  Trash2,
  UserPlus,
} from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { ListPagination } from "@/components/admin/list-pagination"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { cn } from "@/lib/utils"
import type { AdminPagination, BlocklistEvent } from "@/types/admin"

interface AuditHistoryPanelProps {
  events: BlocklistEvent[]
  pagination: AdminPagination
  onPageChange: (page: number) => void
}

/**
 * AuditHistoryPanel — collapsible reverse-chronological audit log on
 * `Admin/Blocklist/Index` (OKS-15 AC7 + UX Spec v2.1 §3-§4).
 *
 * Rendering is uniform across all 3 action variants per the v6 TRD:
 *   - `added`         → user-plus icon, green tone, secondary "Reason: <reason_after>"
 *   - `reason_edited` → pencil-line icon, amber tone, secondary "Was: <reason_before>" / "Now: <reason_after>"
 *   - `removed`       → trash-2 icon, red tone, secondary "Was: <reason_before>"
 *
 * TypeScript narrows the `BlocklistEvent` union by `action`, so each
 * branch consumes `reason_before` / `reason_after` directly (no null-
 * guards) — Backend's DB check constraints lock the nullability
 * invariant.
 *
 * Test IDs (locked v6 contract): `admin-blocklist-audit-toggle`,
 * `admin-blocklist-audit-panel`, `admin-blocklist-audit-event-{index}`.
 *
 * Pagination uses a distinct `events_page` URL param so the audit
 * paginator doesn't collide with the entries paginator above it.
 */
export function AuditHistoryPanel({
  events,
  pagination,
  onPageChange,
}: AuditHistoryPanelProps) {
  const { t, i18n } = useTranslation()
  const [open, setOpen] = useState(false)

  const dateTimeFmt = new Intl.DateTimeFormat(i18n.language, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
  })

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="border-t pt-4"
      data-testid="admin-blocklist-audit-panel"
    >
      <CollapsibleTrigger
        render={
          <button
            type="button"
            className="text-foreground hover:text-foreground/80 inline-flex items-center gap-2 text-sm font-medium"
            data-testid="admin-blocklist-audit-toggle"
          />
        }
      >
        {open ? (
          <ChevronDown className="size-4" aria-hidden="true" />
        ) : (
          <ChevronRight className="size-4" aria-hidden="true" />
        )}
        {open
          ? t("admin.blocklist.audit.toggle_hide")
          : t("admin.blocklist.audit.toggle_show")}
        <span className="text-muted-foreground text-xs font-normal">
          ({pagination.total})
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-4">
        {events.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            {t("admin.blocklist.audit.empty")}
          </p>
        ) : (
          <ul className="flex flex-col gap-3" role="list">
            {events.map((event, index) => (
              <li
                key={event.id}
                className="border-l-2 pl-3 text-sm"
                data-testid={`admin-blocklist-audit-event-${index}`}
                data-event-id={event.id}
                data-event-action={event.action}
              >
                <AuditRow event={event} dateTimeFmt={dateTimeFmt} />
              </li>
            ))}
          </ul>
        )}
        <div className="pt-4">
          <ListPagination
            page={pagination.page}
            perPage={pagination.per_page}
            total={pagination.total}
            onPageChange={onPageChange}
            testId="admin-blocklist-audit-pagination"
          />
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

interface AuditRowProps {
  event: BlocklistEvent
  dateTimeFmt: Intl.DateTimeFormat
}

function AuditRow({ event, dateTimeFmt }: AuditRowProps) {
  const { t } = useTranslation()
  const verb = t(`admin.blocklist.audit.action_verb.${event.action}`)
  const when = dateTimeFmt.format(new Date(event.at))

  // Discriminated render — TypeScript narrows reason_before / reason_after
  // per branch. Each variant has a uniform secondary line per UX Spec v2.1.
  switch (event.action) {
    case "added":
      return (
        <>
          <Primary
            icon={UserPlus}
            tone="green"
            actor={event.actor.name}
            verb={verb}
            handle={event.handle}
            when={when}
          />
          <p className="text-muted-foreground mt-1 text-xs">
            <span className="font-medium">
              {t("admin.blocklist.audit.reason_label")}
            </span>{" "}
            {event.reason_after}
          </p>
        </>
      )
    case "reason_edited":
      return (
        <>
          <Primary
            icon={PencilLine}
            tone="amber"
            actor={event.actor.name}
            verb={verb}
            handle={event.handle}
            when={when}
          />
          <div className="text-muted-foreground mt-1 grid gap-0.5 text-xs">
            <p>
              <span className="font-medium">
                {t("admin.blocklist.audit.was_label")}
              </span>{" "}
              {event.reason_before}
            </p>
            <p>
              <span className="font-medium">
                {t("admin.blocklist.audit.now_label")}
              </span>{" "}
              {event.reason_after}
            </p>
          </div>
        </>
      )
    case "removed":
      return (
        <>
          <Primary
            icon={Trash2}
            tone="red"
            actor={event.actor.name}
            verb={verb}
            handle={event.handle}
            when={when}
          />
          <p className="text-muted-foreground mt-1 text-xs">
            <span className="font-medium">
              {t("admin.blocklist.audit.was_label")}
            </span>{" "}
            {event.reason_before}
          </p>
        </>
      )
  }
}

interface PrimaryProps {
  icon: React.ComponentType<{ className?: string; "aria-hidden"?: "true" }>
  tone: "green" | "amber" | "red"
  actor: string
  verb: string
  /** Null when the event's blocklist entry no longer resolves — see `Handle`. */
  handle: string | null
  when: string
}

function Primary({
  icon: Icon,
  tone,
  actor,
  verb,
  handle,
  when,
}: PrimaryProps) {
  const toneClass =
    tone === "green"
      ? "text-emerald-600 dark:text-emerald-400"
      : tone === "amber"
        ? "text-amber-600 dark:text-amber-400"
        : "text-destructive"
  return (
    <div className="flex items-center gap-2">
      <Icon className={cn("size-4 shrink-0", toneClass)} aria-hidden="true" />
      <span>
        <span className="text-foreground font-medium">{actor}</span>{" "}
        <span className="text-muted-foreground">{verb}</span>{" "}
        <Handle handle={handle} />{" "}
        <span className="text-muted-foreground">— {when}</span>
      </span>
    </div>
  )
}

/**
 * The event's handle, or a neutral stand-in when its blocklist entry no
 * longer resolves (OKS-900).
 *
 * The row is NOT dropped when the handle is missing: an audit trail that
 * silently shortens is worse than one that visibly admits a gap, and dropping
 * rows would desynchronise the list length from the paginator total.
 *
 * Colour is not the sole indicator of the degraded state — the label text
 * says so itself, with muted/italic styling as reinforcement only (UX Spec
 * §6). No tooltip and no action: the admin neither caused this nor can fix
 * it, so inviting interaction would only imply otherwise.
 */
function Handle({ handle }: { handle: string | null }) {
  const { t } = useTranslation()

  if (handle === null) {
    return (
      <span
        className="text-muted-foreground italic"
        data-testid="admin-blocklist-audit-handle-unavailable"
      >
        {t("admin.blocklist.audit.handle_unavailable")}
      </span>
    )
  }

  return <span className="text-foreground font-medium">@{handle}</span>
}
